import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  API, authHeaders, PageHeading, AdminCard,
  Modal, ModalHeader, TH, TD, inputStyle,
} from "./AdminUtils";

const EMPTY_FORM = {
  label: "",
  discountPercent: "",
  startDate: "",
  endDate: "",
};

function SaleStatusBadge({ active }) {
  return (
    <span style={{
      fontSize: 9, fontFamily: "var(--font-mono)", letterSpacing: "0.08em",
      textTransform: "uppercase", padding: "2px 9px",
      borderRadius: "var(--radius-pill)",
      background: active ? "var(--success-bg)" : "var(--bg-secondary)",
      color: active ? "var(--success-text)" : "var(--text-muted)",
      border: `1px solid ${active ? "var(--success-border)" : "var(--border-subtle)"}`,
      display: "inline-block",
    }}>
      {active ? "● Live" : "Inactive"}
    </span>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 10,
  color: "var(--text-muted)",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontFamily: "var(--font-mono)",
  marginBottom: 6,
};

export default function AdminSales() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sale`, { headers: authHeaders() });
      setSales(Array.isArray(res.data) ? res.data : []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => { setFormOpen(false); setFormError(""); };

  const handleSave = async () => {
    if (!form.label.trim()) { setFormError("Label is required."); return; }
    const pct = Number(form.discountPercent);
    if (!form.discountPercent || isNaN(pct) || pct <= 0 || pct > 100) {
      setFormError("Enter a valid discount percent (1–100)."); return;
    }
    if (!form.startDate) { setFormError("Start date is required."); return; }
    if (!form.endDate) { setFormError("End date is required."); return; }
    if (form.endDate < form.startDate) { setFormError("End date must be after start date."); return; }

    setSaving(true);
    setFormError("");
    try {
      await axios.post(
        `${API}/admin/sale`,
        { label: form.label.trim(), discountPercent: pct, startDate: form.startDate, endDate: form.endDate },
        { headers: authHeaders() }
      );
      closeForm();
      load();
    } catch (err) {
      const msg = err.response?.data;
      setFormError(typeof msg === "string" && msg ? msg : "Failed to create sale.");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id) => {
    setActionLoading(id + "-activate");
    try {
      await axios.post(`${API}/admin/sale/${id}/activate`, null, { headers: authHeaders() });
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (id) => {
    setActionLoading(id + "-deactivate");
    try {
      await axios.post(`${API}/admin/sale/${id}/deactivate`, null, { headers: authHeaders() });
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete sale "${label}"? This cannot be undone.`)) return;
    setActionLoading(id + "-delete");
    try {
      await axios.delete(`${API}/admin/sale/${id}`, { headers: authHeaders() });
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div style={{ animation: "auModalIn 0.3s ease both" }}>
      <PageHeading
        title="Global Sales"
        sub="Create and manage site-wide sale events — only one can be live at a time"
        action={
          <button
            onClick={openCreate}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "9px 18px", borderRadius: "var(--radius-md)",
              background: "var(--accent)", border: "none",
              color: "var(--bg-primary)", fontSize: 12,
              letterSpacing: "0.06em", cursor: "pointer",
              fontFamily: "var(--font-mono)",
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Sale
          </button>
        }
      />

      <AdminCard>
        {loading ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Loading…
          </div>
        ) : sales.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>No sales yet.</p>
            <button onClick={openCreate} style={{
              padding: "8px 16px", borderRadius: "var(--radius-md)",
              background: "var(--accent-bg)", border: "1px solid var(--accent)",
              color: "var(--accent)", fontSize: 12, cursor: "pointer",
              fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
            }}>
              Create your first sale
            </button>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <TH>Label</TH>
                <TH>Discount</TH>
                <TH>Start Date</TH>
                <TH>End Date</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {sales.map(s => {
                const isActivating   = actionLoading === s.id + "-activate";
                const isDeactivating = actionLoading === s.id + "-deactivate";
                const isDeleting     = actionLoading === s.id + "-delete";
                return (
                  <tr key={s.id}>
                    <TD>
                      <span style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 13 }}>
                        {s.label}
                      </span>
                    </TD>
                    <TD>
                      <span style={{
                        fontFamily: "var(--font-mono)", fontSize: 13,
                        color: "var(--accent)", fontWeight: 600,
                      }}>
                        {s.discountPercent}% OFF
                      </span>
                    </TD>
                    <TD style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
                      {s.startDate}
                    </TD>
                    <TD style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
                      {s.endDate}
                    </TD>
                    <TD><SaleStatusBadge active={s.active} /></TD>
                    <TD>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {s.active ? (
                          <button
                            onClick={() => handleDeactivate(s.id)}
                            disabled={isDeactivating}
                            style={{
                              padding: "5px 10px", borderRadius: "var(--radius-sm)",
                              background: "var(--warning-bg)", border: "1px solid var(--warning-border)",
                              color: "var(--warning-text)", fontSize: 11,
                              cursor: isDeactivating ? "not-allowed" : "pointer",
                              opacity: isDeactivating ? 0.6 : 1,
                              fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                            }}
                          >
                            {isDeactivating ? "…" : "Deactivate"}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(s.id)}
                            disabled={isActivating}
                            style={{
                              padding: "5px 10px", borderRadius: "var(--radius-sm)",
                              background: "var(--success-bg)", border: "1px solid var(--success-border)",
                              color: "var(--success-text)", fontSize: 11,
                              cursor: isActivating ? "not-allowed" : "pointer",
                              opacity: isActivating ? 0.6 : 1,
                              fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                            }}
                          >
                            {isActivating ? "…" : "Activate"}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(s.id, s.label)}
                          disabled={isDeleting}
                          style={{
                            padding: "5px 10px", borderRadius: "var(--radius-sm)",
                            background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
                            color: "var(--danger-text)", fontSize: 11,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            opacity: isDeleting ? 0.6 : 1,
                            fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                          }}
                        >
                          {isDeleting ? "…" : "Delete"}
                        </button>
                      </div>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </AdminCard>

      {/* ── Create Modal ── */}
      {formOpen && (
        <Modal onClose={closeForm} width={460}>
          <ModalHeader title="New Sale" onClose={closeForm} />

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={labelStyle}>Sale Label *</label>
              <input
                style={inputStyle}
                placeholder="e.g. Festival Sale"
                value={form.label}
                onChange={e => setF("label", e.target.value)}
              />
            </div>

            <div>
              <label style={labelStyle}>Discount Percent * (1–100)</label>
              <input
                style={inputStyle}
                type="number"
                min="1"
                max="100"
                placeholder="e.g. 20"
                value={form.discountPercent}
                onChange={e => setF("discountPercent", e.target.value)}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Start Date *</label>
                <input
                  style={inputStyle}
                  type="date"
                  value={form.startDate}
                  onChange={e => setF("startDate", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>End Date *</label>
                <input
                  style={inputStyle}
                  type="date"
                  value={form.endDate}
                  onChange={e => setF("endDate", e.target.value)}
                />
              </div>
            </div>

            <div style={{
              padding: "10px 14px",
              borderRadius: "var(--radius-sm)",
              background: "var(--bg-secondary)",
              borderLeft: "2px solid var(--accent)",
              fontSize: 11,
              color: "var(--text-muted)",
              lineHeight: 1.6,
            }}>
              Sales are created <strong style={{ color: "var(--text-secondary)" }}>inactive</strong> by default.
              Activate separately when ready — activating auto-deactivates any currently live sale.
            </div>

            {formError && (
              <p style={{
                fontSize: 12, color: "var(--danger-text)", padding: "8px 12px",
                background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
                borderRadius: "var(--radius-sm)",
              }}>
                {formError}
              </p>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
              <button
                onClick={closeForm}
                style={{
                  padding: "9px 20px", borderRadius: "var(--radius-md)",
                  background: "transparent", border: "1px solid var(--border-subtle)",
                  color: "var(--text-muted)", fontSize: 12, cursor: "pointer",
                  fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "9px 24px", borderRadius: "var(--radius-md)",
                  background: "var(--accent)", border: "none",
                  color: "var(--bg-primary)", fontSize: 12,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.7 : 1,
                  fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                }}
              >
                {saving ? "Creating…" : "Create Sale"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
